import type {
  AdminCarbonLedgerEntry,
  AdminImpactMetric,
  AdminFulfillmentTask,
  AdminIntakeLead,
  AdminIntakeQuote,
  AdminInspectionTemplate,
  AdminMarketInsight,
  AdminNotificationMessage,
  AdminOpportunity,
  AdminOpportunityImportRun,
  AdminOrder,
  AdminPartnerOffer,
  AdminPaymentRecord,
  AdminPriceEvidence,
  AdminPricingGuide,
  AdminProcurementExecutionItem,
  AdminProcurementPaymentRecord,
  AdminProcurementOrder,
  AdminProduct,
  AdminProductInspectionReport,
  AdminReadiness,
  AdminOverview,
  AdminRefurbishmentTask,
  AdminServiceCase,
  AdminSupplierPartner,
  AdminUnitEconomics,
  AdminTradeLedger,
  AdminWatchRule,
  BootstrapPayload,
  CarbonLedgerEntryType,
  Category,
  CustomerOrder,
  FulfillmentEventType,
  FulfillmentStatus,
  FulfillmentTaskType,
  InspectionItem,
  InspectionTemplateSeverity,
  InspectionTemplateStatus,
  Product,
  PublicItemDetail,
  ImpactMetric,
  IntakeEventType,
  IntakeQuoteStatus,
  MarketInsight,
  Opportunity,
  PartnerOfferEventType,
  PartnerOfferStatus,
  PaymentChannel,
  PaymentStatus,
  PaymentType,
  PriceEvidenceSource,
  ProcurementContactChannel,
  ProcurementContactPickupMethod,
  ProcurementContactResult,
  ProcurementEventType,
  ProcurementPaymentType,
  ProcurementPaymentStatus,
  ProcurementSellerCredit,
  ProcurementSellerResponseQuality,
  ProcurementSellerRiskLevel,
  ProcurementSellerType,
  ProcurementSourceType,
  ProcurementStatus,
  ProductInspectionStatus,
  ProductStatus,
  RefurbishmentEventType,
  RefurbishmentStatus,
  RefurbishmentTaskType,
  ServiceCaseEventType,
  ServiceCaseStatus,
  ShippingMethod,
  SupplierPartnerStatus,
  SupplierPartnerType,
  TradePlatform,
  WatchRule
} from "./types";

export type AccessSession = {
  role: "admin" | "customer";
  accessToken: string;
  expiresIn: number;
};

export const API_ERROR_EVENT = "yoyo:api-error";

export type ApiErrorIssue = {
  path: Array<string | number>;
  message: string;
};

export type ApiErrorNotice = {
  path: string;
  method: string;
  status: number;
  code: string;
  message: string;
  detail: string;
  issues: ApiErrorIssue[];
};

const apiErrorLabels: Record<string, string> = {
  network_error: "网络连接失败",
  http_error: "请求未完成",
  validation_error: "请求参数校验失败",
  unauthorized: "登录已失效，请重新登录",
  forbidden: "没有执行此操作的权限",
  invalid_credentials: "管理员账号或密码不正确",
  order_verification_failed: "手机号与订单信息不匹配",
  login_rate_limited: "尝试次数过多，请稍后再试",
  admin_not_configured: "管理员登录尚未配置",
  customer_login_not_configured: "订单查询服务尚未配置",
  ledger_rate_limited: "操作过于频繁，请稍后再试",
  database_not_configured: "服务器数据库未配置",
  invalid_ledger_datetime: "日期时间格式无效",
  resale_quantity_exceeds_procurement: "销售数量超过采购剩余数量",
  procurement_order_cancelled: "关联采购单已取消",
  procurement_order_has_sales: "采购单已有销售记录，不能再编辑",
  resale_order_not_found: "销售单不存在",
  resale_order_transition_not_allowed: "销售单状态不允许此操作",
  xianyu_visible_samples_empty: "没有解析到有效的闲鱼样本",
  product_not_available: "商品当前不可用",
  item_not_found: "物品档案不存在",
  intake_lead_not_found: "回收线索不存在",
  intake_quote_not_found: "报价单不存在",
  intake_quote_already_converted: "报价单已转为采购单",
  intake_quote_not_convertible: "报价单当前不可转采购",
  service_case_not_found: "售后工单不存在",
  order_not_found: "订单不存在",
  fulfillment_task_not_found: "履约任务不存在",
  supplier_partner_not_found: "供给伙伴不存在",
  partner_offer_not_found: "伙伴报价不存在",
  opportunity_not_found: "机会记录不存在",
  procurement_order_not_found: "采购单不存在",
  refurbishment_task_not_found: "整备任务不存在",
  product_not_found: "商品不存在",
  inspection_template_not_found: "质检模板不存在",
  product_inspection_report_not_found: "质检报告不存在",
  internal_error: "服务器内部错误"
};

export class ApiError extends Error {
  readonly notice: ApiErrorNotice;

  constructor(notice: ApiErrorNotice) {
    super(notice.detail);
    this.name = "ApiError";
    this.notice = notice;
  }
}

function emitApiError(notice: ApiErrorNotice) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<ApiErrorNotice>(API_ERROR_EVENT, { detail: notice }));
  }
}

function issueDetails(issues: ApiErrorIssue[]) {
  return issues.map((issue) => `${issue.path.length > 0 ? issue.path.join(".") : "参数"}：${issue.message}`);
}

async function parseResponsePayload(response: Response) {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: text.slice(0, 240) };
  }
}

function createApiError(path: string, method: string, status: number, payload: unknown) {
  const body = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  const rawCode = typeof body.error === "string" ? body.error : "http_error";
  const code = /^[a-z0-9_]+$/.test(rawCode) ? rawCode : "http_error";
  const serverMessage = typeof body.message === "string" ? body.message.trim().slice(0, 240) : "";
  const issues = Array.isArray(body.issues)
    ? body.issues.flatMap((issue) => {
      if (!issue || typeof issue !== "object") {
        return [];
      }
      const value = issue as Record<string, unknown>;
      return [{
        path: Array.isArray(value.path) ? value.path.filter((part): part is string | number => typeof part === "string" || typeof part === "number") : [],
        message: typeof value.message === "string" ? value.message : "字段值无效"
      }];
    })
    : [];
  const label = apiErrorLabels[code] ?? (status >= 500 ? "服务器处理失败" : "请求未完成");
  const details = issueDetails(issues);
  const detail = [`${label}（${status}）`, `方法：${method}`, `接口：${path}`, `错误码：${code}`, serverMessage ? `服务端：${serverMessage}` : "", ...details]
    .filter(Boolean)
    .join(" · ");
  return new ApiError({ path, method, status, code, message: label, detail, issues });
}

export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toString().toUpperCase();
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    const error = new ApiError({
      path,
      method,
      status: 0,
      code: "network_error",
      message: "网络连接失败",
      detail: `网络连接失败 · 方法：${method} · 接口：${path} · 请检查网络或服务器状态`,
      issues: []
    });
    emitApiError(error.notice);
    throw error;
  }

  const payload = await parseResponsePayload(response);
  if (!response.ok) {
    const error = createApiError(path, method, response.status, payload);
    emitApiError(error.notice);
    throw error;
  }
  return payload as T;
}

export async function loginAdmin(username: string, password: string): Promise<AccessSession> {
  return loginRequest("/api/auth/admin/login", { username, password });
}

export async function loginCustomer(phone: string, orderId: number): Promise<AccessSession> {
  return loginRequest("/api/auth/customer/login", { phone, orderId });
}

export async function loadCustomerOrders(token: string): Promise<CustomerOrder[]> {
  return requestJson<CustomerOrder[]>("/api/customer/orders", {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` }
  });
}

async function loginRequest(path: string, body: Record<string, unknown>): Promise<AccessSession> {
  return requestJson<AccessSession>(path, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}

export type AppVersion = {
  ok: boolean;
  service: string;
  name: string;
  version: string;
  buildId: string;
  storefront: string;
  hotUpdate: boolean;
  houses: string[];
};

export type StorefrontPayload = AppVersion & {
  houses: Array<{
    id: string;
    name: string;
    brand: string;
    focus: string;
    promise: string;
    warranty: string;
    reject: string;
    listedCount: number;
    products: Product[];
  }>;
  otherProducts: Product[];
  products: Product[];
  sourcingPulse: Array<{
    id: string;
    name: string;
    watching: number;
    inspect: number;
    bid: number;
    total: number;
  }>;
};

export async function loadAppVersion(): Promise<AppVersion> {
  return requestJson<AppVersion>("/api/app-version", {
    headers: { Accept: "application/json" }
  });
}

export async function loadStorefront(): Promise<StorefrontPayload> {
  return requestJson<StorefrontPayload>("/api/storefront", {
    headers: { Accept: "application/json" }
  });
}

export type OperatorRadarPayload = {
  opportunities: Opportunity[];
  watchRules: WatchRule[];
  sourcingPulse: BootstrapPayload["sourcingPulse"];
  sourceStatus: NonNullable<BootstrapPayload["sourceStatus"]>;
};

export async function loadOperatorRadar(token: string): Promise<OperatorRadarPayload> {
  return adminRequest<OperatorRadarPayload>("/api/admin/radar", token);
}

export async function loadBootstrap(): Promise<BootstrapPayload> {
  return requestJson<BootstrapPayload>("/api/bootstrap", {
    headers: { Accept: "application/json" }
  });
}

export async function loadPublicItem(publicToken: string): Promise<PublicItemDetail> {
  return requestJson<PublicItemDetail>(`/api/items/${encodeURIComponent(publicToken)}`, {
    headers: { Accept: "application/json" }
  });
}

async function adminRequest<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  return requestJson<T>(path, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {})
    }
  });
}

export async function loadAdminDashboard(token: string) {
  const [
    overview,
    unitEconomics,
    readiness,
    products,
    inspectionTemplates,
    orders,
    paymentRecords,
    fulfillmentTasks,
    serviceCases,
    intakeLeads,
    intakeQuotes,
    notifications,
    opportunities,
    opportunityImportRuns,
    supplierPartners,
    partnerOffers,
    procurementExecutionItems,
    procurementOrders,
    procurementPaymentRecords,
    refurbishmentTasks,
    productInspectionReports,
    watchRules,
    marketInsights,
    impactMetrics,
    carbonLedgerEntries,
    priceEvidence
  ] = await Promise.all([
    adminRequest<AdminOverview>("/api/admin/overview", token),
    adminRequest<AdminUnitEconomics>("/api/admin/unit-economics?limit=20", token),
    adminRequest<AdminReadiness>("/api/admin/readiness", token),
    adminRequest<AdminProduct[]>("/api/admin/products?limit=20", token),
    adminRequest<AdminInspectionTemplate[]>("/api/admin/inspection-templates?limit=20", token),
    adminRequest<AdminOrder[]>("/api/admin/orders?limit=20", token),
    adminRequest<AdminPaymentRecord[]>("/api/admin/payment-records?limit=20", token),
    adminRequest<AdminFulfillmentTask[]>("/api/admin/fulfillment-tasks?limit=20", token),
    adminRequest<AdminServiceCase[]>("/api/admin/service-cases?limit=20", token),
    adminRequest<AdminIntakeLead[]>("/api/admin/intake-leads?limit=20", token),
    adminRequest<AdminIntakeQuote[]>("/api/admin/intake-quotes?limit=20", token),
    adminRequest<AdminNotificationMessage[]>("/api/admin/notifications?limit=40", token),
    adminRequest<AdminOpportunity[]>("/api/admin/opportunities?limit=20", token),
    adminRequest<AdminOpportunityImportRun[]>("/api/admin/opportunity-import-runs?limit=20", token),
    adminRequest<AdminSupplierPartner[]>("/api/admin/supplier-partners?limit=20", token),
    adminRequest<AdminPartnerOffer[]>("/api/admin/partner-offers?limit=20", token),
    adminRequest<AdminProcurementExecutionItem[]>("/api/admin/procurement-execution?limit=20", token),
    adminRequest<AdminProcurementOrder[]>("/api/admin/procurement-orders?limit=20", token),
    adminRequest<AdminProcurementPaymentRecord[]>("/api/admin/procurement-payment-records?limit=20", token),
    adminRequest<AdminRefurbishmentTask[]>("/api/admin/refurbishment-tasks?limit=20", token),
    adminRequest<AdminProductInspectionReport[]>("/api/admin/product-inspection-reports?limit=20", token),
    adminRequest<AdminWatchRule[]>("/api/admin/watch-rules?limit=20", token),
    adminRequest<AdminMarketInsight[]>("/api/admin/market-insights?limit=20", token),
    adminRequest<AdminImpactMetric[]>("/api/admin/impact-metrics?limit=20", token),
    adminRequest<AdminCarbonLedgerEntry[]>("/api/admin/carbon-ledger?limit=20", token),
    adminRequest<AdminPriceEvidence[]>("/api/admin/price-evidence?limit=20", token)
  ]);

  return {
    overview,
    unitEconomics,
    readiness,
    products,
    inspectionTemplates,
    orders,
    paymentRecords,
    fulfillmentTasks,
    serviceCases,
    intakeLeads,
    intakeQuotes,
    notifications,
    opportunities,
    opportunityImportRuns,
    supplierPartners,
    partnerOffers,
    procurementExecutionItems,
    procurementOrders,
    procurementPaymentRecords,
    refurbishmentTasks,
    productInspectionReports,
    watchRules,
    marketInsights,
    impactMetrics,
    carbonLedgerEntries,
    priceEvidence
  };
}

export async function createAdminInspectionTemplate(
  token: string,
  template: {
    category: Category;
    subcategory: string;
    templateName: string;
    version: number;
    status: InspectionTemplateStatus;
    operatorNote?: string;
    items: Array<{
      label: string;
      requirement: string;
      severity: InspectionTemplateSeverity;
    }>;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/inspection-templates", token, {
    method: "POST",
    body: JSON.stringify(template)
  });
}

export async function updateAdminInspectionTemplateStatus(
  token: string,
  id: number,
  status: InspectionTemplateStatus,
  operatorNote = ""
) {
  return adminRequest<{ updated: number }>(`/api/admin/inspection-templates/${id}/status`, token, {
    method: "POST",
    body: JSON.stringify({ status, operatorNote })
  });
}

export async function createAdminFulfillmentTask(
  token: string,
  task: {
    orderId: number;
    taskType: FulfillmentTaskType;
    city: string;
    address: string;
    scheduledAt?: string | null;
    assignee?: string;
    status?: FulfillmentStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/fulfillment-tasks", token, {
    method: "POST",
    body: JSON.stringify(task)
  });
}

export async function recordAdminFulfillmentEvent(
  token: string,
  id: number,
  event: {
    status: FulfillmentStatus;
    eventType?: FulfillmentEventType;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/fulfillment-tasks/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function updateAdminOrderStatus(token: string, id: number, status: AdminOrder["status"]) {
  return adminRequest<{ updated: number }>(`/api/admin/orders/${id}/status`, token, {
    method: "PATCH",
    body: JSON.stringify({ status })
  });
}

export async function createAdminPaymentRecord(
  token: string,
  payment: {
    orderId: number;
    paymentType: PaymentType;
    amount: number;
    channel: PaymentChannel;
    externalRef?: string | null;
    status?: PaymentStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/payment-records", token, {
    method: "POST",
    body: JSON.stringify(payment)
  });
}

export async function recordAdminServiceCaseEvent(
  token: string,
  id: number,
  event: {
    status: ServiceCaseStatus;
    eventType?: ServiceCaseEventType;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/service-cases/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function createServiceCase(caseInput: {
  orderId?: number | null;
  productId?: string | null;
  customerName: string;
  contact: string;
  issueType: AdminServiceCase["issue_type"];
  description: string;
}) {
  return requestJson<{ id: number }>("/api/service-cases", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(caseInput)
  });
}

export async function updateAdminIntakeStatus(token: string, id: number, status: AdminIntakeLead["status"]) {
  return adminRequest<{ updated: number }>(`/api/admin/intake-leads/${id}/status`, token, {
    method: "PATCH",
    body: JSON.stringify({ status })
  });
}

export async function recordAdminIntakeEvent(
  token: string,
  id: number,
  event: {
    eventType: IntakeEventType;
    amount?: number | null;
    scheduledAt?: string | null;
    productId?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/intake-leads/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function createAdminIntakeQuote(
  token: string,
  quote: {
    intakeLeadId: number;
    quoteAmount: number;
    expectedListPrice?: number | null;
    validUntil?: string | null;
    status?: Exclude<IntakeQuoteStatus, "converted">;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/intake-quotes", token, {
    method: "POST",
    body: JSON.stringify(quote)
  });
}

export async function updateAdminIntakeQuoteStatus(
  token: string,
  id: number,
  status: Exclude<IntakeQuoteStatus, "converted">,
  operatorNote = ""
) {
  return adminRequest<{ id: number }>(`/api/admin/intake-quotes/${id}/status`, token, {
    method: "POST",
    body: JSON.stringify({ status, operatorNote })
  });
}

export async function convertAdminIntakeQuoteToProcurement(
  token: string,
  id: number,
  input: {
    paymentStatus?: ProcurementPaymentStatus;
    status?: ProcurementStatus;
    operatorNote?: string;
  } = {}
) {
  return adminRequest<{ id: number }>(`/api/admin/intake-quotes/${id}/convert`, token, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function createAdminProduct(token: string, product: Omit<Product, "id"> & { id?: string }) {
  return adminRequest<{ id: string }>("/api/admin/products", token, {
    method: "POST",
    body: JSON.stringify(product)
  });
}

export async function updateAdminProductStatus(
  token: string,
  productId: string,
  status: ProductStatus,
  operatorNote = ""
) {
  return adminRequest<{ id: number }>(`/api/admin/products/${productId}/status`, token, {
    method: "POST",
    body: JSON.stringify({ status, operatorNote })
  });
}

export async function refreshAdminOpportunities(token: string) {
  return adminRequest<{ runId: number; imported: number; alerted: number; opportunities: unknown[] }>(
    "/api/admin/opportunities/refresh",
    token,
    { method: "POST" }
  );
}

export type XianyuVisibleSampleInput = {
  title: string;
  itemUrl: string;
  askPrice: number;
  fairPrice?: number;
  category: Category;
  subcategory: string;
  city: string;
  condition: string;
  sellerSignal?: string;
  observedAt?: string;
};

export type XianyuVisibleSampleDefaults = {
  category: Category;
  subcategory: string;
  city: string;
  condition: string;
};

export type XianyuVisibleSamplePreview = {
  summary: {
    totalRows: number;
    validRows: number;
    rejectedRows: number;
  };
  validSamples: XianyuVisibleSampleInput[];
  rejectedRows: Array<{
    rowNumber: number;
    rawText: string;
    reason: string;
  }>;
};

export type XianyuVisibleImportResult = {
  opportunityRunId: number;
  priceEvidenceRunId: number;
  importedOpportunities: number;
  importedPriceEvidence: number;
  alerted: number;
  opportunities: Opportunity[];
};

export type XianyuVisibleImportAndProcurementResult = XianyuVisibleImportResult & {
  procurementOrders: Array<{
    opportunityId: string;
    id: number;
    existing: boolean;
  }>;
};

export async function previewAdminXianyuVisibleSamples(
  token: string,
  text: string,
  defaults: XianyuVisibleSampleDefaults
) {
  return adminRequest<XianyuVisibleSamplePreview>("/api/admin/opportunities/preview-xianyu-visible", token, {
    method: "POST",
    body: JSON.stringify({ text, ...defaults })
  });
}

export async function importAdminXianyuVisibleSamples(token: string, samples: XianyuVisibleSampleInput[]) {
  return adminRequest<XianyuVisibleImportResult>("/api/admin/opportunities/import-xianyu-visible", token, {
    method: "POST",
    body: JSON.stringify({ samples })
  });
}

export async function importAdminXianyuVisibleSampleText(
  token: string,
  text: string,
  defaults: XianyuVisibleSampleDefaults
) {
  return adminRequest<XianyuVisibleImportResult>("/api/admin/opportunities/import-xianyu-visible", token, {
    method: "POST",
    body: JSON.stringify({ text, ...defaults })
  });
}

export async function importAdminXianyuVisibleSampleTextAndConvertToProcurement(
  token: string,
  text: string,
  defaults: XianyuVisibleSampleDefaults
): Promise<XianyuVisibleImportAndProcurementResult> {
  const imported = await importAdminXianyuVisibleSampleText(token, text, defaults);
  const procurementOrders = [];

  for (const opportunity of imported.opportunities) {
    const result = await convertAdminOpportunityToProcurement(token, opportunity.id, {
      quantity: 1,
      agreedPrice: opportunity.askPrice,
      estimatedListPrice: opportunity.fairPrice,
      paymentStatus: "unpaid",
      status: "negotiating",
      operatorNote: `${opportunity.title} · ${opportunity.city} · 可见页导入后自动转采购跟进；未联系卖家，未付款`
    });
    procurementOrders.push({
      opportunityId: opportunity.id,
      id: result.id,
      existing: result.existing
    });
  }

  return {
    ...imported,
    procurementOrders
  };
}

export async function createAdminOpportunity(
  token: string,
  opportunity: {
    id?: string;
    title: string;
    category?: Opportunity["category"];
    city?: string;
    askPrice: number;
    fairPrice: number;
    sellerSignal?: string;
    publishedAt: string;
    sourceUrl: string;
  }
) {
  return adminRequest<{ opportunity: Opportunity; alerted: number }>("/api/admin/opportunities", token, {
    method: "POST",
    body: JSON.stringify(opportunity)
  });
}

export async function recordAdminOpportunityAction(
  token: string,
  id: string,
  action: NonNullable<AdminOpportunity["latest_action"]>,
  operatorNote = ""
) {
  return adminRequest<{ id: number }>(`/api/admin/opportunities/${id}/actions`, token, {
    method: "POST",
    body: JSON.stringify({ action, operatorNote })
  });
}

export async function convertAdminOpportunityToProcurement(
  token: string,
  id: string,
  input: {
    quantity?: number;
    agreedPrice?: number | null;
    estimatedListPrice?: number | null;
    paymentStatus?: ProcurementPaymentStatus;
    status?: ProcurementStatus;
    operatorNote?: string;
  } = {}
) {
  return adminRequest<{ id: number; existing: boolean }>(`/api/admin/opportunities/${id}/convert-to-procurement`, token, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function createAdminSupplierPartner(
  token: string,
  partner: {
    name: string;
    partnerType: SupplierPartnerType;
    city: string;
    contactName?: string;
    contact: string;
    status?: SupplierPartnerStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/supplier-partners", token, {
    method: "POST",
    body: JSON.stringify(partner)
  });
}

export async function updateAdminSupplierPartnerStatus(
  token: string,
  id: number,
  status: SupplierPartnerStatus,
  operatorNote = ""
) {
  return adminRequest<{ updated: number }>(`/api/admin/supplier-partners/${id}/status`, token, {
    method: "POST",
    body: JSON.stringify({ status, operatorNote })
  });
}

export async function createAdminPartnerOffer(
  token: string,
  offer: {
    partnerId: number;
    category: Category;
    subcategory: string;
    title: string;
    quantity?: number;
    targetPrice: number;
    city: string;
    status?: PartnerOfferStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/partner-offers", token, {
    method: "POST",
    body: JSON.stringify(offer)
  });
}

export async function recordAdminPartnerOfferEvent(
  token: string,
  id: number,
  event: {
    status: PartnerOfferStatus;
    eventType?: PartnerOfferEventType;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/partner-offers/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function convertAdminPartnerOfferToProcurement(
  token: string,
  id: number,
  input: {
    agreedPrice?: number | null;
    estimatedListPrice?: number | null;
    paymentStatus?: ProcurementPaymentStatus;
    status?: ProcurementStatus;
    operatorNote?: string;
  } = {}
) {
  return adminRequest<{ id: number; existing: boolean }>(`/api/admin/partner-offers/${id}/convert-to-procurement`, token, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function loadAdminTradeLedger(token: string, limit = 50) {
  return adminRequest<AdminTradeLedger>(`/api/admin/trade-ledger?limit=${limit}`, token);
}

export async function createAdminTradeProcurement(
  token: string,
  input: {
    platform: TradePlatform;
    category: Category;
    subcategory?: string;
    color?: string;
    giftInfo?: string;
    title: string;
    city?: string;
    quantity: number;
    buyPrice: number;
    shippingMethod: ShippingMethod;
    freight: number;
    consumableCost?: number;
    miscCost?: number;
    externalRef?: string | null;
    purchasedAt?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/trade-ledger/procurements", token, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function updateAdminTradeProcurement(
  token: string,
  id: number,
  input: {
    platform: TradePlatform;
    category: Category;
    subcategory?: string;
    color?: string;
    giftInfo?: string;
    title: string;
    city?: string;
    quantity: number;
    buyPrice: number;
    shippingMethod: ShippingMethod;
    freight: number;
    consumableCost?: number;
    miscCost?: number;
    externalRef?: string | null;
    purchasedAt?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ updated: number }>(`/api/admin/trade-ledger/procurements/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
}

export async function createAdminTradeSale(
  token: string,
  input: {
    procurementOrderId: number;
    platform: TradePlatform;
    giftInfo?: string;
    quantity: number;
    salePrice: number;
    shippingMethod: ShippingMethod;
    freight: number;
    consumableCost?: number;
    platformFee?: number;
    miscCost?: number;
    externalRef?: string | null;
    listedAt?: string | null;
    soldAt?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number; margin: number }>("/api/admin/trade-ledger/sales", token, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function updateAdminTradeSaleStatus(
  token: string,
  id: number,
  input: {
    status: "sold" | "cancelled";
    soldAt?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ updated: number }>(`/api/admin/trade-ledger/sales/${id}/status`, token, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
}

export async function createAdminProcurementOrder(
  token: string,
  order: {
    sourceType: ProcurementSourceType;
    sourceRef?: string;
    category: Category;
    title: string;
    city: string;
    quantity?: number;
    agreedPrice: number;
    estimatedListPrice?: number | null;
    paymentStatus?: ProcurementPaymentStatus;
    status?: ProcurementStatus;
    productId?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/procurement-orders", token, {
    method: "POST",
    body: JSON.stringify(order)
  });
}

export async function createAdminProcurementPaymentRecord(
  token: string,
  payment: {
    procurementOrderId: number;
    payeeName?: string;
    paymentType?: ProcurementPaymentType;
    amount: number;
    freightCost?: number;
    paidAt?: string | null;
    dealAt?: string | null;
    channel?: PaymentChannel;
    externalRef?: string | null;
    status?: PaymentStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/procurement-payment-records", token, {
    method: "POST",
    body: JSON.stringify(payment)
  });
}

export async function recordAdminProcurementEvent(
  token: string,
  id: number,
  event: {
    status: ProcurementStatus;
    paymentStatus?: ProcurementPaymentStatus;
    eventType?: ProcurementEventType;
    amount?: number | null;
    productId?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/procurement-orders/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function createAdminProcurementContactAttempt(
  token: string,
  procurementOrderId: number,
  input: {
    channel: ProcurementContactChannel;
    result: ProcurementContactResult;
    sellerResponse: string;
    confirmedPrice?: number | null;
    pickupMethod?: ProcurementContactPickupMethod;
    sellerType?: ProcurementSellerType;
    sellerCredit?: ProcurementSellerCredit;
    sameSkuCount?: number;
    usesRealPhotos?: boolean;
    supportsVideo?: boolean;
    supportsEscrow?: boolean;
    supportsInspection?: boolean;
    asksOffPlatformPayment?: boolean;
    refusesVerification?: boolean;
    priceAnomaly?: boolean;
    responseQuality?: ProcurementSellerResponseQuality;
    nextFollowUpAt?: string | null;
    riskFlags?: string[];
    operatorNote?: string;
  }
) {
  return adminRequest<{
    id: number;
    eventId: number;
    riskScore: number;
    riskLevel: ProcurementSellerRiskLevel;
    hardBlocked: boolean;
  }>(
    `/api/admin/procurement-orders/${procurementOrderId}/contact-attempts`,
    token,
    {
      method: "POST",
      body: JSON.stringify(input)
    }
  );
}

export async function notifyAdminProcurementExecutionQueue(
  token: string,
  input: {
    limit?: number;
  } = {}
) {
  return adminRequest<{ evaluated: number; notified: number; inserted: number }>(
    "/api/admin/procurement-execution/notify",
    token,
    {
      method: "POST",
      body: JSON.stringify(input)
    }
  );
}

export async function createAdminRefurbishmentTask(
  token: string,
  task: {
    procurementOrderId?: number | null;
    productId?: string | null;
    taskType: RefurbishmentTaskType;
    category: Category;
    title: string;
    city: string;
    assignee?: string;
    estimatedCost?: number;
    actualCost?: number | null;
    status?: RefurbishmentStatus;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/refurbishment-tasks", token, {
    method: "POST",
    body: JSON.stringify(task)
  });
}

export async function recordAdminRefurbishmentEvent(
  token: string,
  id: number,
  event: {
    status: RefurbishmentStatus;
    eventType?: RefurbishmentEventType;
    actualCost?: number | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>(`/api/admin/refurbishment-tasks/${id}/events`, token, {
    method: "POST",
    body: JSON.stringify(event)
  });
}

export async function createAdminProductInspectionReport(
  token: string,
  report: {
    productId: string;
    templateId?: number | null;
    refurbishmentTaskId?: number | null;
    category: Category;
    inspector?: string;
    reportStatus?: ProductInspectionStatus;
    safetyScore?: number;
    summary: string;
    operatorNote?: string;
    items: Array<{
      label: string;
      result: InspectionItem["result"];
      severity: InspectionTemplateSeverity;
      detail: string;
    }>;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/product-inspection-reports", token, {
    method: "POST",
    body: JSON.stringify(report)
  });
}

export async function updateAdminProductInspectionReportStatus(
  token: string,
  id: number,
  reportStatus: ProductInspectionStatus,
  operatorNote = ""
) {
  return adminRequest<{ id: number }>(`/api/admin/product-inspection-reports/${id}/status`, token, {
    method: "POST",
    body: JSON.stringify({ reportStatus, operatorNote })
  });
}

export async function createAdminWatchRule(token: string, rule: Omit<WatchRule, "id"> & { id?: string }) {
  return adminRequest<{ id: string }>("/api/admin/watch-rules", token, {
    method: "POST",
    body: JSON.stringify(rule)
  });
}

export async function markAdminNotificationRead(token: string, id: number) {
  return adminRequest<{ updated: number }>(`/api/admin/notifications/${id}/read`, token, {
    method: "POST",
    body: JSON.stringify({})
  });
}

export async function createAdminMarketInsight(
  token: string,
  insight: Omit<MarketInsight, "id"> & { id?: string }
) {
  return adminRequest<{ id: string }>("/api/admin/market-insights", token, {
    method: "POST",
    body: JSON.stringify(insight)
  });
}

export async function createAdminImpactMetric(
  token: string,
  metric: ImpactMetric & { sortOrder?: number }
) {
  return adminRequest<{ label: string }>("/api/admin/impact-metrics", token, {
    method: "POST",
    body: JSON.stringify(metric)
  });
}

export async function createAdminCarbonLedgerEntry(
  token: string,
  entry: {
    entryType: CarbonLedgerEntryType;
    category: Category;
    productId?: string | null;
    orderId?: number | null;
    procurementOrderId?: number | null;
    refurbishmentTaskId?: number | null;
    city: string;
    carbonKg: number;
    evidenceTitle: string;
    evidenceUrl?: string | null;
    accountingMethod: string;
    operatorNote?: string;
    occurredAt?: string | null;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/carbon-ledger", token, {
    method: "POST",
    body: JSON.stringify(entry)
  });
}

export async function createAdminPriceEvidence(
  token: string,
  evidence: {
    category: Category;
    subcategory: string;
    city: string;
    condition: string;
    observedPrice: number;
    source: PriceEvidenceSource;
    sourceTitle: string;
    sourceUrl?: string | null;
    evidenceAt?: string | null;
    operatorNote?: string;
  }
) {
  return adminRequest<{ id: number }>("/api/admin/price-evidence", token, {
    method: "POST",
    body: JSON.stringify(evidence)
  });
}

export async function importAdminXianyuPriceEvidence(token: string) {
  return adminRequest<{ runId: number; imported: number }>("/api/admin/price-evidence/import-xianyu", token, {
    method: "POST"
  });
}

export async function loadAdminPricingGuide(
  token: string,
  input: {
    category: Category;
    subcategory: string;
    city?: string | null;
    condition?: string | null;
  }
) {
  const params = new URLSearchParams({
    category: input.category,
    subcategory: input.subcategory
  });

  if (input.city) {
    params.set("city", input.city);
  }

  if (input.condition) {
    params.set("condition", input.condition);
  }

  return adminRequest<AdminPricingGuide>(`/api/admin/pricing-guide?${params.toString()}`, token);
}
